import React, { useState, useEffect, useRef } from "react";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Dummy data for metrics and alerts
const initialMetrics = [
  { label: "Events Processed", value: 12458, id: "eventsCounter", suffix: "" },
  { label: "P99 Latency", value: 3.2, id: "p99Latency", suffix: "ms" },
  { label: "Success Rate", value: 99.99, id: "successRate", suffix: "%" },
  { label: "Throughput", value: 2.4, id: "throughput", suffix: "GB/s" },
  { label: "Active Nodes", value: "12/12", id: "nodesActive", suffix: "" },
  { label: "Partitions", value: 256, id: "partitions", suffix: "" },
];

const initialAlerts = [
  {
    severity: "high",
    title: "High Latency Detected on Producer-3",
    meta: "Partition: transactions-5 | Current: 450ms (Threshold: 200ms) | 2 min ago",
    desc: "Latency spike detected in the transaction processing pipeline. This may affect end-user experience.",
    icon: "fas fa-exclamation-triangle",
    isNew: true,
  },
  {
    severity: "high",
    title: "Disk Space Critical",
    meta: "Broker-2 | Usage: 95% (45GB/50GB) | 1 hour ago",
    desc: "Broker-2 is running low on disk space. Consider cleaning up old logs or adding more storage.",
    icon: "fas fa-database",
    isNew: false,
  },
  {
    severity: "medium",
    title: "High CPU Usage",
    meta: "Broker-1 | 85% CPU | 3 hours ago",
    desc: "Sustained high CPU usage detected on Broker-1. Monitor for potential performance degradation.",
    icon: "fas fa-microchip",
    isNew: false,
  },
  {
    severity: "medium",
    title: "Network Latency Increase",
    meta: "Zone: us-east-1a | Avg: 120ms | 5 hours ago",
    desc: "Network latency between data centers has increased by 40% in the last hour.",
    icon: "fas fa-network-wired",
    isNew: false,
  },
  {
    severity: "low",
    title: "Memory Usage Approaching Threshold",
    meta: "Broker-3 | 75% Memory | 8 hours ago",
    desc: "Memory usage is approaching the warning threshold. Monitor for potential memory pressure.",
    icon: "fas fa-memory",
    isNew: false,
  },
  {
    severity: "info",
    title: "New Consumer Group Added",
    meta: "Group: analytics-service | 15 hours ago",
    desc: "A new consumer group 'analytics-service' has started consuming from topic 'user-events'.",
    icon: "fas fa-users",
    isNew: false,
  },
  {
    severity: "info",
    title: "Configuration Update",
    meta: "Environment: Production | 1 day ago",
    desc: "Kafka configuration updated. New retention period: 7 days.",
    icon: "fas fa-cogs",
    isNew: false,
  },
  {
    severity: "low",
    title: "Security Patch Applied",
    meta: "Version: 2.8.1 | 2 days ago",
    desc: "Security patch applied successfully. CVE-2023-1234 has been addressed.",
    icon: "fas fa-shield-alt",
    isNew: false,
  },
  {
    severity: "alert",
    title: "Critical System Failure",
    meta: "Node: broker-1 | 5 minutes ago",
    desc: "A critical failure has occurred in broker-1. Immediate attention required.",
    icon: "fas fa-exclamation-circle",
    isNew: true,
  }
];


const analyticsData =[
    {
        topicName: 'transactions.raw',
        stat: '45.2K msgs/s',
        trend: {
            direction: 'up',
            valuepercent: 12
        }
    },
    {
        topicName: 'logs.security',
        stat: '28.7K msgs/s',
        trend: {
            direction: 'down',
            valuepercent: 3
        }
    },
    {
        topicName: 'user.events',
        stat: '15.1K msgs/s',
        trend: {
            direction: 'up',
            valuepercent: 8
        }
    },
    {
        topicName: 'metrics.system',
        stat: '8.3K msgs/s',
        trend: {
            direction: 'up',
            valuepercent: 2
        }
    },
    {
        topicName: 'notifications',
        stat: '4.9K msgs/s',
        trend: {
            direction: 'up',
            valuepercent: 0
        }
    }
];



const tabList = ["dashboard", "alerts", "analytics"];

function Hero() {
  // Tab state and rotation
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isRotating, setIsRotating] = useState(true);
  const rotationRef = useRef();

  // Metric animation state
  const [metrics, setMetrics] = useState(initialMetrics.map(m => ({ ...m, animated: typeof m.value === "number" ? 0 : m.value })));

  // Alert filter/search state
  const [alertFilter, setAlertFilter] = useState("all");
  const [alertSearch, setAlertSearch] = useState("");

  // Tab auto-rotation effect
  useEffect(() => {
    if (!isRotating) return;
    rotationRef.current = setInterval(() => {
      setActiveTab(prev => {
        const idx = tabList.indexOf(prev);
        return tabList[(idx + 1) % tabList.length];
      });
    }, 8000);
    return () => clearInterval(rotationRef.current);
  }, [isRotating]);

  // Pause rotation on tab click/hover
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setIsRotating(false);
    setTimeout(() => setIsRotating(true), 10000);
  };

  // Animate metrics on mount or tab switch
  useEffect(() => {
    if (activeTab !== "dashboard") return;
    const anims = [];
    setMetrics(metrics => metrics.map(m => ({ ...m, animated: typeof m.value === "number" ? 0 : m.value })));
    metrics.forEach((metric, i) => {
      if (typeof metric.value !== "number") return;
      const duration = 1200 + i * 200;
      const start = 0;
      const end = metric.value;
      const step = (end - start) / (duration / 30);
      let current = start;
      anims[i] = setInterval(() => {
        current += step;
        setMetrics(metrics => metrics.map((m, idx) => idx === i ? { ...m, animated: current >= end ? end : current } : m));
        if (current >= end) clearInterval(anims[i]);
      }, 30);
    });
    return () => anims.forEach(clearInterval);
    // eslint-disable-next-line
  }, [activeTab]);

  // Alerts filtering/search
  const filteredAlerts = initialAlerts.filter(alert => {
    const matchesFilter = alertFilter === "all" || alert.severity === alertFilter;
    const matchesSearch = alert.title.toLowerCase().includes(alertSearch.toLowerCase()) || alert.desc.toLowerCase().includes(alertSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Chart data for dashboard
  const numericMetrics = metrics.filter(m => typeof m.value === 'number');
  const chartData = {
    labels: numericMetrics.map(m => m.label),
    datasets: [
      {
        label: 'Metrics',
        data: numericMetrics.map(m => m.value),
        backgroundColor: 'rgba(30, 144, 255, 0.7)',
        borderRadius: 8,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } },
    maintainAspectRatio: false,
  };

  return (
    <section className="hero">
      <div className="container">
        <div className="hero-content">
          <h1 className="hero-title">Stream. Detect. Decide. With AI. Now.</h1>
          <p className="hero-subtitle">
            Build real-time, secure, and compliant data-in-motion architectures with <b>iValue's Confluent Center of Excellence </b> where Confluent Kafka, Flink, and governance converge to power next-generation banking, cybersecurity, and analytics.
          </p>
          <div className="hero-buttons cta-buttons">
            <a href="/contact" className="btn btn-primary">Talk to Our Experts</a>
            <a href="#usecases" className="btn btn-outline">Explore Use Cases</a>
          </div>
          <div className="key-messages">
            <div className="key-message">
              <i className="fas fa-bolt"></i>
              <span>Turn data into live decisions — from transactions to threat signals</span>
            </div>
            <div className="key-message">
              <i className="fas fa-chart-line"></i>
              <span>Unlock new revenue and compliance efficiency with real-time streaming</span>
            </div>
            <div className="key-message">
              <i className="fas fa-certificate"></i>
              <span>Co-engineered by iValue experts with certified Confluent architects</span>
            </div>
          </div>
        </div>

        <div className="hero-visual" data-aos="fade-left">
          <div className="hero-visual-inner">
            <div className="data-stream">
              <div className="data-point" style={{ "--delay": "0.1s" }}></div>
              <div className="data-point" style={{ "--delay": "0.3s" }}></div>
              <div className="data-point" style={{ "--delay": "0.5s" }}></div>
              <div className="data-point" style={{ "--delay": "0.2s" }}></div>
              <div className="data-point" style={{ "--delay": "0.4s" }}></div>
            </div>

            <div className="dashboard-preview">
              <div className="dashboard-header">
                <div className="dashboard-tabs">
                  {tabList.map(tab => (
                    <div className="tab-btn-container" key={tab}>
                      <span
                        className={`tab-btn${activeTab === tab ? " active" : ""}`}
                        data-tab={tab}
                        aria-selected={activeTab === tab}
                        onClick={() => handleTabClick(tab)}
                        onMouseEnter={() => setIsRotating(false)}
                        onMouseLeave={() => setIsRotating(true)}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        <div className="tab-progress-container">
                          <div className="tab-progress" style={activeTab === tab && isRotating ? { animation: `progress 8s linear infinite` } : {}}></div>
                        </div>
                      </span>
                    </div>
                  ))}
                </div>
                <div className={`rotation-indicator${isRotating ? " playing" : " paused"}`}>
                  <span className="icon">{isRotating ? "▶️" : "⏸️"}</span>
                  <span className="text">{isRotating ? "Auto-rotating" : "Paused"}</span>
                </div>
              </div>

              {/* Dashboard Tab */}
              <div className={`tab-content${activeTab === "dashboard" ? " active" : ""}`} id="dashboard-tab">
                <div className="dashboard-metrics">
                  {metrics.map((metric, i) => (
                    <div className="metric" data-aos="fade-up" data-aos-delay={100 + i * 50} key={metric.id}>
                      <div className="metric-value" id={metric.id}>
                        {typeof metric.value === "number"
                          ? Number(metric.animated).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : metric.value}
                        {metric.suffix && <small>{metric.suffix}</small>}
                      </div>
                      <div className="metric-label">{metric.label}</div>
                    </div>
                  ))}
                </div>
                <div className="chart-container" style={{ minHeight: 220 }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>

              {/* Alerts Tab */}
              <div className={`tab-content${activeTab === "alerts" ? " active" : ""}`} id="alerts-tab">
                <div className="alerts-filters">
                  <div className="filter-group">
                    <span className="filter-label">Filter by:</span>
                    {[
                      { label: "All Alerts", value: "all" },
                      { label: "High", value: "high" },
                      { label: "Medium", value: "medium" },
                      { label: "Low", value: "low" },
                      { label: "Info", value: "info" },
                    ].map(btn => (
                      <button
                        className={`filter-btn${alertFilter === btn.value ? " active" : ""}`}
                        data-filter={btn.value}
                        key={btn.value}
                        onClick={() => setAlertFilter(btn.value)}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                  <div className="search-box">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search alerts..."
                      value={alertSearch}
                      onChange={e => setAlertSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="alerts-container">
                  {filteredAlerts.length === 0 && (
                    <div className="alert-item">
                      <div className="alert-content">
                        <div className="alert-title">No alerts found</div>
                      </div>
                    </div>
                  )}
                  {filteredAlerts.map((alert, idx) => (
                    <div
                      className={`alert-item${alert.isNew ? " new" : ""}`}
                      data-severity={alert.severity}
                      key={alert.title + idx}
                    >
                      <div className="alert-icon"><i className={alert.icon}></i></div>
                      <div className="alert-content">
                        <div className="alert-title">{alert.title}</div>
                        <div className="alert-meta">{alert.meta}</div>
                        <div className="alert-desc">{alert.desc}</div>
                      </div>
                      <div className="alert-actions">
                        <button className="btn-icon" title="Acknowledge"><i className="fas fa-check"></i></button>
                        <button className="btn-icon" title="Assign"><i className="fas fa-user-plus"></i></button>
                      </div>
                      <div className={`alert-severity ${alert.severity}`}>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Analytics Tab - placeholder for future chart/JSX */}
              <div className={`tab-content${activeTab === "analytics" ? " active" : ""}`} id="analytics-tab">
                <div className="analytics-grid">
                    {analyticsData.map((data, index) => (
                        <div className="analytics-card redesigned-card" key={index}>
                          <div className="analytics-card-body">
                            <div className="analytics-icon-section">
                              <span className="analytics-icon">
                                <i className={data.topicName.includes('log') ? 'fas fa-shield-alt' : 'fas fa-exchange-alt'}></i>
                              </span>
                            </div>
                            <div className="analytics-info-section">
                              <div className="analytics-topic">{data.topicName}</div>
                              <div className="analytics-stat">{data.stat}</div>
                            </div>
                            <div className="analytics-trend-section">
                              <span className={`trend-indicator ${data.trend.direction}`}>
                                {data.trend.direction === 'up' ? (
                                  <span className="trend-arrow up">▲</span>
                                ) : (
                                  <span className="trend-arrow down">▼</span>
                                )}
                                <span className="trend-value">{data.trend.valuepercent}%</span>
                              </span>
                            </div>
                          </div>
                        </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-pattern"></div>
    </section>
  );
}

export default Hero;
